import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { compact, isEqual } from 'lodash-es';
import { netTermsOptions } from '~/constants';
import { approvalMethodNoticePeriodModel, paymentMethodModel, relationshipModel, serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { ServiceChangeItemType } from '~/models/service/model';
import { ServiceCostType } from '~/models/service-cost/model';
import { formatDate } from '~/models/time';
import { utils } from '~/utils';
import pluralize from '~/utils/pluralize';
export const netTermsSelectOptions = netTermsOptions.map((numOfDays)=>({
        value: numOfDays,
        text: numOfDays === 0 ? 'on acceptance' : pluralize('day', numOfDays, true)
    }));
export function getAllowManualPaymentsChangeBullet(relationship, allowManualPayments) {
    return allowManualPayments === relationship.allowManualPayments ? null : {
        text: getAllowManualPaymentsChangeText(relationship.allowManualPayments, allowManualPayments),
        requiresApproval: !allowManualPayments
    };
}
export function getAllowManualPaymentsChangeText(origValue, newValue) {
    const from = getAllowManualPaymentsText(origValue);
    const to = getAllowManualPaymentsText(newValue);
    return 'Manual approval of each payment - changed from "'.concat(from, '" to "').concat(to, '"');
}
export function getAllowManualPaymentsText(allowManualPayments) {
    return allowManualPayments ? 'Required' : 'Not required';
}
export function getCoverCCFeesChangeBullet(relationship, coverCreditCardFees) {
    const origCoverCCFees = relationshipModel.isVendorCoveringCCFee(relationship);
    const isCreditCard = !!relationship.paymentMethod && paymentMethodModel.isCreditCard(relationship.paymentMethod);
    if (coverCreditCardFees === origCoverCCFees) {
        return null;
    } else {
        const vendorBusinessName = relationshipModel.vendorName(relationship);
        const clientBusinessName = relationshipModel.customerName(relationship);
        return {
            text: getCoverCCFeesChangeText(origCoverCCFees, coverCreditCardFees, vendorBusinessName, clientBusinessName),
            requiresApproval: isCreditCard && !coverCreditCardFees
        };
    }
}
export function getCoverCCFeesChangeText(oldCoverCCFees, newCoverCCFees, vendorBusinessName, clientBusinessName) {
    const from = getCoverCCFeesText(oldCoverCCFees, vendorBusinessName, clientBusinessName);
    const to = getCoverCCFeesText(newCoverCCFees, vendorBusinessName, clientBusinessName);
    return 'Card processing fees - changed from "'.concat(from, '" to "').concat(to, '"');
}
export function getCoverCCFeesText(coverCreditCardFees, vendorBusinessName, clientBusinessName) {
    const name = coverCreditCardFees ? "".concat(vendorBusinessName || 'service provider') : "".concat(clientBusinessName || 'client');
    return "Paid by ".concat(name);
}
export function getNetTermsChangeBullet(relationship, updatedNetTerms) {
    if (updatedNetTerms === relationship.netTerms) {
        return null;
    }
    const beforeText = netTermsSelectOptions.find((opt)=>opt.value === relationship.netTerms).text;
    const afterText = netTermsSelectOptions.find((opt)=>opt.value === updatedNetTerms).text;
    return {
        text: "Payment terms changed from ".concat(beforeText, " to ").concat(afterText),
        requiresApproval: updatedNetTerms < relationship.netTerms
    };
}
export function getInvoicesApprovalMethodChangeBullet(originalApprovalMethod, newApprovalMethod) {
    const originalNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(originalApprovalMethod);
    const newNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(newApprovalMethod);
    if (originalNoticePeriod != null) {
        if (newNoticePeriod != null) {
            return newNoticePeriod !== originalNoticePeriod ? {
                text: "Out of scope charges approval - notice period changed from ".concat(pluralize('day', originalNoticePeriod, true), " to ").concat(pluralize('day', newNoticePeriod, true)),
                requiresApproval: newNoticePeriod < originalNoticePeriod
            } : null;
        } else {
            return {
                text: 'Out of scope charges approval - changed from auto-approval with a notice period to manual approval',
                requiresApproval: false
            };
        }
    } else {
        if (newNoticePeriod != null) {
            return {
                text: "Out of scope charges approval - changed from manual to auto-approval with a notice period of ".concat(pluralize('day', newNoticePeriod, true)),
                requiresApproval: true
            };
        } else {
            return null;
        }
    }
}
export function getAmendmentsApprovalMethodChangeBullet(originalApprovalMethod, newApprovalMethod) {
    const originalNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(originalApprovalMethod);
    const newNoticePeriod = approvalMethodNoticePeriodModel.noticePeriodDays(newApprovalMethod);
    if (originalNoticePeriod != null) {
        if (newNoticePeriod != null) {
            return newNoticePeriod !== originalNoticePeriod ? {
                text: "Amendments approval - notice period changed from ".concat(pluralize('day', originalNoticePeriod, true), " to ").concat(pluralize('day', newNoticePeriod, true)),
                requiresApproval: newNoticePeriod < originalNoticePeriod
            } : null;
        } else {
            return {
                text: 'Amendments approval - changed from auto-approval with a notice period to manual approval',
                requiresApproval: false
            };
        }
    } else {
        if (newNoticePeriod != null) {
            return {
                text: "Amendments approval - changed from manual to auto-approval with a notice period of ".concat(pluralize('day', newNoticePeriod, true)),
                requiresApproval: true
            };
        } else {
            return null;
        }
    }
}
export function getServiceChangeBullets(service, SERVICE_TEMPLATE_BUNDLE) {
    if (serviceModel.isAwaitingApproval(service.status)) {
        return [
            {
                text: SERVICE_TEMPLATE_BUNDLE ? "".concat(service.name, " has been added") : 'A new service has been added',
                requiresApproval: true
            }
        ];
    }
    return serviceModel.makeServiceChangeList(service).map((item)=>{
        switch(item.type){
            case ServiceChangeItemType.Description:
                {
                    return {
                        text: SERVICE_TEMPLATE_BUNDLE ? 'Description was changed' : 'Service description was changed',
                        requiresApproval: item.requiresApproval
                    };
                }
            case ServiceChangeItemType.IncludedServicesDescription:
                {
                    return {
                        text: 'Included services - description was changed',
                        requiresApproval: item.requiresApproval
                    };
                }
            case ServiceChangeItemType.Discount:
                {
                    var _service_cost_discount, _item_value;
                    const noDiscount = 'none';
                    const fromDiscount = ((_service_cost_discount = service.cost.discount) === null || _service_cost_discount === void 0 ? void 0 : _service_cost_discount.format()) || noDiscount;
                    const toDiscount = ((_item_value = item.value) === null || _item_value === void 0 ? void 0 : _item_value.format()) || noDiscount;
                    return {
                        text: "Discount was changed from ".concat(fromDiscount, " to ").concat(toDiscount),
                        requiresApproval: item.requiresApproval
                    };
                }
            case ServiceChangeItemType.BillingTrigger:
                {
                    const oldBillingCycle = getBillingCycle(service.billing);
                    const newBillingCycle = getBillingCycle(item.value);
                    const oldBillingMode = getBillingMode(service.billing);
                    const newBillingMode = getBillingMode(item.value);
                    const oldBillingDayOfMonth = serviceModel.billingDayOfMonth(service);
                    const newBillingDayOfMonth = serviceBillingModel.billingDayOfMonth(item.value);
                    const oldBillingDayOfWeek = serviceModel.billingDayOfWeek(service);
                    const newBillingDayOfWeek = serviceBillingModel.billingDayOfWeek(item.value);
                    return compact([
                        oldBillingCycle !== newBillingCycle && {
                            text: "Billing cycle was changed from ".concat(oldBillingCycle, " to ").concat(newBillingCycle),
                            requiresApproval: item.requiresApproval
                        },
                        oldBillingMode !== newBillingMode && {
                            text: "Billing trigger was changed from ".concat(oldBillingMode, " to ").concat(newBillingMode),
                            requiresApproval: false,
                            emailWontBeSent: true
                        },
                        oldBillingDayOfMonth != null && newBillingDayOfMonth != null && !isEqual(oldBillingDayOfMonth, newBillingDayOfMonth) && {
                            text: "Billing day - changed from ".concat(oldBillingDayOfMonth.formatDay(), " to ").concat(newBillingDayOfMonth.formatDay()),
                            requiresApproval: false
                        },
                        oldBillingDayOfWeek != null && newBillingDayOfWeek != null && oldBillingDayOfWeek !== newBillingDayOfWeek && {
                            text: "Billing day - changed from ".concat(oldBillingDayOfWeek, " to ").concat(newBillingDayOfWeek),
                            requiresApproval: false
                        }
                    ]);
                }
            case ServiceChangeItemType.Pricing:
                {
                    const originalPrice = getPrice(service.cost);
                    const newPrice = getPrice(_object_spread_props(_object_spread({}, item.value), {
                        discount: null
                    }));
                    return compact([
                        originalPrice !== newPrice && {
                            text: "Price was changed from ".concat(originalPrice, " to ").concat(newPrice),
                            requiresApproval: item.requiresApproval
                        }
                    ]);
                }
            case ServiceChangeItemType.PriceIncrease:
                {
                    const prevPriceIncrease = serviceModel.priceIncrease(service);
                    const text = getPriceIncreaseBulletText(prevPriceIncrease, item.value);
                    return {
                        text,
                        requiresApproval: item.requiresApproval
                    };
                }
        }
    }).flat(1);
}
function getPriceIncreaseBulletText(prevPriceIncrease, itemValue) {
    if (prevPriceIncrease.exists()) {
        if (itemValue.exists()) {
            return "Automatic price increase was changed from ".concat(getPriceIncreaseText(prevPriceIncrease), " to ").concat(getPriceIncreaseText(itemValue));
        }
        return "Automatic price increase of ".concat(getPriceIncreaseText(prevPriceIncrease), " was removed");
    }
    if (itemValue.exists()) {
        return "Automatic price increase of ".concat(getPriceIncreaseText(itemValue), " was added");
    }
    return '';
}
function getPriceIncreaseText(priceIncrease) {
    const increaseOn = priceIncrease.increaseOn();
    const percentIncrease = priceIncrease.percentIncrease();
    return "".concat(serviceModel.isInvalidPercentIncrease(percentIncrease) ? '' : percentIncrease, "% every year ").concat(increaseOn ? "starting on ".concat(formatDate(increaseOn)) : 'on agreement anniversary');
}
function getBillingCycle(billing) {
    return serviceBillingModel.isRecurring(billing) ? serviceModel.recurrencePeriod(billing) : 'one time';
}
function getBillingMode(billing) {
    return serviceBillingModel.isAutomatic(billing) ? 'automatic' : 'manual';
}
function getPrice(cost) {
    switch(cost.pricingType){
        case ServiceCostType.Fixed:
            return utils.formatDollarAmount(cost.price);
        case ServiceCostType.Range:
            return "a range from ".concat(utils.formatDollarAmount(cost.minPrice || 0), " to ").concat(utils.formatDollarAmount(cost.maxPrice));
        case ServiceCostType.Variable:
            return compact([
                "".concat(utils.formatDollarAmount(cost.price), "/").concat(serviceCostModel.formattedUnitName(cost)),
                cost.unitCap && "(".concat(utils.formatNumberWithCommas(cost.unitCap), " preapproved)")
            ]).join(' ');
    }
}
